import { useSelector } from 'react-redux';
import { Sparkles, BookOpen } from 'lucide-react';

/**
 * ApplyLayout — two-column shell for individual /apply pages.
 *
 * NOTE: This layout intentionally does NOT render its own header, progress
 * strip, or floating assistants. Those live in LoanApplicationPage so we have
 * exactly one source of truth for navigation and ambient UI. ApplyLayout is
 * just a content frame: title + form column on the left, supportive context
 * column on the right.
 *
 * Backwards-compat: `step`, `totalSteps`, `showProgress`, and `context` are
 * still accepted as props so existing callers keep compiling, but they no
 * longer render anything (the macro stepper in ApplyHeader replaces them).
 */
const ApplyLayout = ({
  children,
  title,
  subtitle,
  contentMaxWidth = 600,
  rightRailContent = null,
  // accepted for back-compat, intentionally unused
  // eslint-disable-next-line no-unused-vars
  step, totalSteps, showProgress, context,
}) => {
  const { readinessScore } = useSelector((state) => state.loanApplication);

  return (
    <div style={{ minHeight: '100%', backgroundColor: 'var(--lk-gray-50)' }}>
      {/* Main content area */}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 'var(--lk-space-8) var(--lk-space-4)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--lk-space-4)' }}>
          {/* LEFT — primary form content */}
          <div>
            <div style={{
              backgroundColor: 'white',
              borderRadius: 'var(--lk-radius-lg)',
              padding: '48px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}>
              {title && (
                <div style={{ marginBottom: 'var(--lk-space-4)', maxWidth: 600 }}>
                  <h1 style={{
                    fontSize: 28, fontWeight: 600,
                    lineHeight: 1.3, marginBottom: 8,
                    color: 'var(--lk-text-primary)',
                  }}>
                    {title}
                  </h1>
                  {subtitle && (
                    <p style={{
                      fontSize: 16, lineHeight: 1.5,
                      color: 'var(--lk-text-tertiary)', marginBottom: 0,
                    }}>
                      {subtitle}
                    </p>
                  )}
                </div>
              )}

              <div style={{ maxWidth: contentMaxWidth === 'full' ? '100%' : contentMaxWidth }}>{children}</div>
            </div>

            {/* Mobile readiness ribbon */}
            {readinessScore > 0 && (
              <div className="lk-readiness-mobile" style={{
                display: 'none', marginTop: 12, padding: '10px 14px',
                backgroundColor: 'var(--lk-primary-50)', borderRadius: 8,
                fontSize: 13, color: 'var(--lk-primary-700)', fontWeight: 600,
              }}>
                Mortgage Readiness Score: {readinessScore}/100
              </div>
            )}
          </div>

          {/* RIGHT — sticky context rail */}
          <div>
            <div style={{ position: 'sticky', top: 88 }}>
              {rightRailContent || (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: 'var(--lk-radius-lg)',
                    padding: 24,
                    border: '1px solid var(--lk-border-subtle)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-3)' }}>
                      <img src="/assets/owen-avatar.png" alt="" style={{ width: 22, height: 22, borderRadius: '50%' }} />
                      <h6 style={{ margin: 0, fontWeight: 600 }}>Owen AI Assistant</h6>
                    </div>
                    <p style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)', marginBottom: 'var(--lk-space-3)' }}>
                      Ask Owen anything about this step, or have him explain why we're collecting the information.
                    </p>
                    <button
                      style={{
                        width: '100%', padding: '8px 12px',
                        border: '1px solid var(--lk-primary-500)',
                        borderRadius: 'var(--lk-radius-md)',
                        background: 'white', color: 'var(--lk-primary-500)',
                        cursor: 'pointer', fontSize: 13, fontWeight: 500,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                      }}
                      onClick={() => {
                        const owenPanel = document.querySelector('[data-owen-panel]');
                        if (owenPanel) owenPanel.click();
                      }}
                    >
                      <Sparkles size={16} /> Ask Owen
                    </button>
                  </div>

                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: 'var(--lk-radius-lg)',
                    padding: 24,
                    border: '1px solid var(--lk-border-subtle)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-3)' }}>
                      <BookOpen size={20} color="var(--lk-primary-400)" />
                      <h6 style={{ margin: 0, fontWeight: 600 }}>Why We Ask This</h6>
                    </div>
                    <p style={{ color: 'var(--lk-text-tertiary)', fontSize: 13, margin: 0 }}>
                      Each question feeds Owen's program router and your readiness score.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .lk-readiness-mobile { display: block !important; }
        }
        @media (max-width: 1024px) {
          /* collapse to single column on tablets */
          .lk-readiness-mobile { display: block !important; }
        }
      `}</style>
    </div>
  );
};

export default ApplyLayout;
